import React from 'react';
import Header from '../Header';
import Banner from '../Products/Banner';
import Footer from '../Footer';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const content = {
  tr: {
    title: 'Hizmetlerimiz',
    description: 'Ürünlerimiz, çözümlerimiz ve hizmetlerimiz ile geleceğe yatırım yapın.',
    breadcrumb: (
      <>
        <Link to="/tr" className="text-gray-400 font-bold">Ana Sayfa</Link>
        <span className="text-gray-400 font-bold">/</span>
        <span className="text-white font-bold">Hizmetlerimiz</span>
      </>
    ),
    footerLang: 'tr',
  },
  en: {
    title: 'Our Services',
    description: 'Invest in the future with our products, solutions and services.',
    breadcrumb: (
      <>
        <Link to="/en" className="text-gray-400 font-bold">Home</Link>
        <span className="text-gray-400 font-bold">/</span>
        <span className="text-white font-bold">Our Services</span>
      </>
    ),
    footerLang: 'en',
  },
};

const Investments: React.FC = () => {
  const location = useLocation();
  const language = location.pathname.startsWith('/en') ? 'en' : 'tr';
  const lang: 'en' | 'tr' = language;
  const page = content[lang];

  const investments = [
    {
      title: 'UyumTakip',
      desc: lang === 'tr'
        ? 'Finansal kuruluşlar ve yükümlü işletmeler için MASAK uyum ve müşteri yönetimi yazılımı. Müşteri tanıma, yaptırım/AML sorgulama, evrak ve işlem takibini tek platformda toplar; uyum süreçlerini otomatikleştirip denetime hazır hale getirir.'
        : 'MASAK compliance and customer management software for financial institutions and obliged businesses. It brings customer onboarding, sanctions/AML screening, document and transaction tracking into a single platform; automating compliance processes and keeping you audit-ready.',
      link: `/${lang}/investment/uyumtakip`,
      image: process.env.PUBLIC_URL + '/uyumtakip-logo.png',
      highlights: lang === 'tr'
        ? [
            { t: 'MASAK & AML', d: 'MASAK, OFAC ve yaptırım listelerinde otomatik sorgulama' },
            { t: 'Müşteri & Evrak', d: 'Dijital KYC, belge süresi ve eksik takibi' },
            { t: 'Entegrasyon', d: 'Banka ve muhasebe (Mikro) entegrasyonu + mutabakat' },
          ]
        : [
            { t: 'MASAK & AML', d: 'Automatic screening against MASAK, OFAC and sanctions lists' },
            { t: 'Customer & Documents', d: 'Digital KYC, document expiry and missing-file tracking' },
            { t: 'Integration', d: 'Bank and accounting (Mikro) integration + reconciliation' },
          ],
    },
  ];

  return (
    <div>
      <Header language={lang} setLanguage={() => {}} />
      <Banner
        title={page.title}
        description={page.description}
        image={process.env.PUBLIC_URL + '/banner.png'}
        breadcrumb={page.breadcrumb}
      />
      
      {/* Yatırımlarımız Bölümü */}
      <motion.section
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        viewport={{ once: false, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
            {lang === 'tr' ? 'Hizmetlerimiz' : 'Our Services'}
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-center max-w-4xl mx-auto">
            {lang === 'tr'
              ? 'Gelişmiş teknoloji çözümleri ile işinizi büyütün'
              : 'Grow your business with advanced technology solutions'}
          </p>
          <div className="flex justify-center">
            {investments.map((investment, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="w-full max-w-md bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group overflow-hidden flex flex-col"
              >
                {/* Logo Bölümü */}
                <div className="h-40 flex items-center justify-center bg-gradient-to-br from-yellow-50 via-white to-gray-50 p-8">
                  <img
                    src={investment.image}
                    alt={investment.title}
                    className="max-h-16 w-auto object-contain"
                  />
                </div>

                {/* İçerik Bölümü */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{investment.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{investment.desc}</p>
                  <ul className="space-y-2 mb-2">
                    {investment.highlights.map((h) => (
                      <li key={h.t} className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        <span className="text-sm text-gray-700"><span className="font-semibold text-gray-900">{h.t}</span> — {h.d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Buton - Kartın içinde en altta */}
                <div className="px-6 pb-6">
                  <Link
                    to={investment.link}
                    className="inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors duration-200 w-full"
                  >
                    {lang === 'tr' ? 'Detaylı Bilgi' : 'Learn More'}
                    <svg className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <Footer language={lang} />
    </div>
  );
};

export default Investments; 